import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import API from '../utils/api';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);
const CART_KEY = 'cherrypkd_cart';

function loadCart() {
  try { return JSON.parse(localStorage.getItem(CART_KEY)) || null; } catch { return null; }
}

export function CartProvider({ children }) {
  const [cartItem,    setCartItem]    = useState(loadCart);   // CherryPKD = single-item checkout
  const [checkoutData,setCheckoutData] = useState({});
  // QA r3: the server cart (/api/cart) needs an account. Logged-out shoppers use only the local
  // cart: no /api/cart call, so no 401 and no error. Kept in a ref so the callbacks stay stable.
  const { user } = useAuth();
  const signedInRef = useRef(!!user);
  signedInRef.current = !!user;

  // UX r4 item 4a: when a shopper logs in or signs up (or opens a new session already signed in)
  // with a cart kept while logged out, merge it into the server cart once per account per page
  // load. The server dedupes and checks status / stock / own listing; an item it reports as
  // unavailable is removed here. The local copy stays as the checkout cart (the server cart is only
  // used for cart reminders and has no read endpoint), so the cart survives login as before.
  const mergedForRef = useRef(null);
  const userId = user?.id || null;
  useEffect(() => {
    if (!userId) { mergedForRef.current = null; return; }
    if (mergedForRef.current === userId) return;
    mergedForRef.current = userId;
    const local = loadCart();
    const listingId = local?.listing?.id;
    if (!listingId) return;
    API.post('/cart/merge', { items: [{ listingId }] })
      .then(({ data }) => {
        const gone = (data?.unavailable || []).find(u => String(u.listingId).toLowerCase() === String(listingId).toLowerCase());
        if (!gone) return;
        const current = loadCart();
        if (current?.listing?.id !== listingId) return; // replaced meanwhile
        setCartItem(null);
        try { localStorage.removeItem(CART_KEY); } catch {}
        toast(gone.reason === 'own_listing'
          ? 'Your own listing was removed from your cart.'
          : `"${local.listing.title || 'An item'}" is no longer available and was removed from your cart.`);
      })
      .catch(() => {});
  }, [userId]);

  const addToCart = useCallback((listing, extras = {}) => {
    if (['sold', 'rented', 'archived'].includes(listing?.status)) {
      return { ok: false, error: 'This item is no longer available.' };
    }
    const item = { listing, ...extras, addedAt: Date.now() };
    setCartItem(item);
    try { localStorage.setItem(CART_KEY, JSON.stringify(item)); } catch {}
    if (signedInRef.current) API.post('/cart', { listingId: listing.id }).catch(() => {});
    return { ok: true };
  }, []);

  const clearCart = useCallback((listingId) => {
    setCartItem(null);
    setCheckoutData({});
    try { localStorage.removeItem(CART_KEY); } catch {}
    if (signedInRef.current) API.delete('/cart', { data: { listingId } }).catch(() => {});
  }, []);

  const setCheckout = useCallback((data) => {
    setCheckoutData(prev => ({ ...prev, ...data }));
  }, []);

  return (
    <CartContext.Provider value={{ cartItem, checkoutData, addToCart, clearCart, setCheckout }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be inside CartProvider');
  return ctx;
};
